// src/pages/Dashboard/Admin/Coaching/hooks/useCoachingDashboard.js

import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import { getAllEnseignants } from "../../../../../services/adherentServices";
import {
  getTeacherEvents,
  unregisterAdherent,
  saveCoachHour,
} from "../../../../../services/eventServices";

import {
  calculateCoachingGlobalStats,
  calculateTeacherStats,
  normalizeTeacherEventsResponse,
  sortEventsByDateDesc,
  buildChartRowsFromTeacherStats,
} from "../utils/coachingDashboardUtils";

async function runWithConcurrency(items, worker, limit = 4) {
  const results = new Array(items.length);
  let cursor = 0;

  const runners = new Array(Math.min(limit, items.length))
    .fill(null)
    .map(async () => {
      while (cursor < items.length) {
        const index = cursor;
        cursor += 1;
        results[index] = await worker(items[index], index);
      }
    });

  await Promise.all(runners);
  return results;
}

function sortTeachers(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort((a, b) =>
    `${a?.nom || ""} ${a?.prenom || ""}`.localeCompare(
      `${b?.nom || ""} ${b?.prenom || ""}`,
      "fr"
    )
  );
}

function sortTeacherStats(rows) {
  return [...(Array.isArray(rows) ? rows : [])].sort(
    (a, b) => Number(b?.totalHours || 0) - Number(a?.totalHours || 0)
  );
}

export function useCoachingDashboard() {
  const [teachers, setTeachers] = useState([]);
  const [selectedTeacherId, setSelectedTeacherId] = useState("");

  const [events, setEvents] = useState([]);
  const [teacherStats, setTeacherStats] = useState([]);

  const [loadingTeachers, setLoadingTeachers] = useState(true);
  const [loadingEvents, setLoadingEvents] = useState(false);
  const [loadingChart, setLoadingChart] = useState(false);

  const [hoursDrawerOpen, setHoursDrawerOpen] = useState(false);
  const [currentEvent, setCurrentEvent] = useState(null);
  const [savingHours, setSavingHours] = useState(false);

  const [savingDeleteId, setSavingDeleteId] = useState(null);

  const fetchTeachers = useCallback(async () => {
    try {
      setLoadingTeachers(true);

      const response = await getAllEnseignants();
      const rows = sortTeachers(response);

      setTeachers(rows);
      return rows;
    } catch (error) {
      console.error("Erreur chargement enseignants :", error);
      toast.error("Erreur lors de la récupération des enseignants.");
      setTeachers([]);
      return [];
    } finally {
      setLoadingTeachers(false);
    }
  }, []);

  const fetchEventsForTeacher = useCallback(
    async (teacherId, options = { updateState: true }) => {
      if (!teacherId) {
        if (options.updateState) setEvents([]);
        return [];
      }

      try {
        if (options.updateState) setLoadingEvents(true);

        const response = await getTeacherEvents(teacherId);
        const rows = sortEventsByDateDesc(
          normalizeTeacherEventsResponse(response)
        );

        if (options.updateState) setEvents(rows);

        return rows;
      } catch (error) {
        console.error("Erreur chargement événements enseignant :", error);
        toast.error("Erreur lors de la récupération des événements.");

        if (options.updateState) setEvents([]);

        return [];
      } finally {
        if (options.updateState) setLoadingEvents(false);
      }
    },
    []
  );

  /**
   * IMPORTANT :
   * Cette fonction ne dépend PAS de `teachers`.
   * Sinon, chaque setTeachers recrée la fonction, relance le useEffect,
   * puis rappelle getAllEnseignants/getTeacherEvents en boucle.
   */
  const fetchTeacherStats = useCallback(async (teacherRows = []) => {
    const sourceRows = Array.isArray(teacherRows) ? teacherRows : [];

    if (sourceRows.length === 0) {
      setTeacherStats([]);
      return [];
    }

    try {
      setLoadingChart(true);

      const rows = await runWithConcurrency(
        sourceRows,
        async (teacher) => {
          try {
            const response = await getTeacherEvents(teacher.id);
            const teacherEvents = normalizeTeacherEventsResponse(response);

            return calculateTeacherStats(teacher, teacherEvents);
          } catch (error) {
            console.error(
              "Erreur stats coaching enseignant :",
              teacher?.id,
              error
            );

            return calculateTeacherStats(teacher, []);
          }
        },
        4
      );

      const sorted = sortTeacherStats(rows);

      setTeacherStats(sorted);
      return sorted;
    } catch (error) {
      console.error("Erreur chargement stats coaching :", error);
      toast.error("Erreur lors du chargement du graphique coaching.");
      setTeacherStats([]);
      return [];
    } finally {
      setLoadingChart(false);
    }
  }, []);

  /**
   * Chargement initial :
   * - 1 appel pour récupérer les enseignants
   * - 1 passage pour construire les stats globales
   *
   * Ne dépend pas de `teachers`, donc pas de boucle infinie.
   */
  useEffect(() => {
    let alive = true;

    (async () => {
      const teacherRows = await fetchTeachers();

      if (!alive) return;

      await fetchTeacherStats(teacherRows);
    })();

    return () => {
      alive = false;
    };
  }, [fetchTeachers, fetchTeacherStats]);

  const selectedTeacher = useMemo(() => {
    return (
      teachers.find(
        (teacher) => String(teacher.id) === String(selectedTeacherId)
      ) || null
    );
  }, [teachers, selectedTeacherId]);

  const updateSelectedTeacherStats = useCallback(
    (teacher, teacherEvents) => {
      if (!teacher?.id) return;

      const nextStat = calculateTeacherStats(teacher, teacherEvents);

      setTeacherStats((prev) => {
        const exists = prev.some(
          (row) => String(row.teacherId) === String(teacher.id)
        );

        const next = exists
          ? prev.map((row) =>
              String(row.teacherId) === String(teacher.id) ? nextStat : row
            )
          : [...prev, nextStat];

        return sortTeacherStats(next);
      });
    },
    []
  );

  const handleTeacherChange = async (teacherId) => {
    setSelectedTeacherId(teacherId);
    setEvents([]);

    if (!teacherId) return;

    const teacher =
      teachers.find((item) => String(item.id) === String(teacherId)) || null;

    const teacherEvents = await fetchEventsForTeacher(teacherId, {
      updateState: true,
    });

    if (teacher) {
      updateSelectedTeacherStats(teacher, teacherEvents);
    }
  };

  const refreshSelectedTeacher = useCallback(async () => {
    if (!selectedTeacherId) return;

    const teacher =
      teachers.find((item) => String(item.id) === String(selectedTeacherId)) ||
      null;

    const teacherEvents = await fetchEventsForTeacher(selectedTeacherId, {
      updateState: true,
    });

    if (teacher) {
      updateSelectedTeacherStats(teacher, teacherEvents);
    }
  }, [
    selectedTeacherId,
    teachers,
    fetchEventsForTeacher,
    updateSelectedTeacherStats,
  ]);

  const refreshAll = useCallback(async () => {
    const teacherRows = await fetchTeachers();

    await fetchTeacherStats(teacherRows);

    if (selectedTeacherId) {
      const teacher =
        teacherRows.find(
          (item) => String(item.id) === String(selectedTeacherId)
        ) || null;

      const teacherEvents = await fetchEventsForTeacher(selectedTeacherId, {
        updateState: true,
      });

      if (teacher) {
        updateSelectedTeacherStats(teacher, teacherEvents);
      }
    }
  }, [
    fetchTeachers,
    fetchTeacherStats,
    fetchEventsForTeacher,
    selectedTeacherId,
    updateSelectedTeacherStats,
  ]);

  const selectedTeacherStats = useMemo(() => {
    return calculateTeacherStats(selectedTeacher, events);
  }, [selectedTeacher, events]);

  const globalStats = useMemo(() => {
    return calculateCoachingGlobalStats(teacherStats);
  }, [teacherStats]);

  const chartRows = useMemo(() => {
    return buildChartRowsFromTeacherStats(teacherStats);
  }, [teacherStats]);

  const openHoursDrawer = (event) => {
    setCurrentEvent(event);
    setHoursDrawerOpen(true);
  };

  const closeHoursDrawer = () => {
    if (savingHours) return;

    setCurrentEvent(null);
    setHoursDrawerOpen(false);
  };

  const handleSaveCoachHour = async (event, coachHour) => {
    if (!event?.AdherentEvent) {
      toast.error("Impossible d’identifier le coach sur cet événement.");
      return;
    }

    const value = Number(coachHour);

    if (!Number.isFinite(value) || value < 0) {
      toast.error("Veuillez saisir un nombre d’heures valide.");
      return;
    }

    try {
      setSavingHours(true);

      await saveCoachHour(
        event.AdherentEvent.adherent_id,
        event.AdherentEvent.event_id,
        { coach_hour: value }
      );

      toast.success("Heures de coaching mises à jour.");

      setCurrentEvent(null);
      setHoursDrawerOpen(false);

      await refreshSelectedTeacher();
    } catch (error) {
      console.error("Erreur sauvegarde heures coaching :", error);
      toast.error("Erreur lors de la mise à jour des heures.");
    } finally {
      setSavingHours(false);
    }
  };

  const handleUnregisterCoach = async (event) => {
    const adherentId = event?.AdherentEvent?.adherent_id;
    const eventId = event?.AdherentEvent?.event_id || event?.id;

    if (!adherentId || !eventId) {
      toast.error("Impossible d’identifier le coach ou l’événement.");
      return;
    }

    if (
      !window.confirm(
        "Voulez-vous vraiment supprimer ce coach de l'événement ? Cela supprimera également les heures de coaching enregistrées."
      )
    ) {
      return;
    }

    try {
      setSavingDeleteId(eventId);

      await unregisterAdherent(adherentId, eventId);

      toast.success("Coach supprimé de l’événement.");
      await refreshSelectedTeacher();
    } catch (error) {
      console.error("Erreur suppression coach événement :", error);
      toast.error("Erreur lors de la suppression du coach de l’événement.");
    } finally {
      setSavingDeleteId(null);
    }
  };

  return {
    teachers,
    selectedTeacherId,
    selectedTeacher,

    events,
    teacherStats,
    chartRows,

    loadingTeachers,
    loadingEvents,
    loadingChart,

    globalStats,
    selectedTeacherStats,

    hoursDrawerOpen,
    currentEvent,
    savingHours,
    savingDeleteId,

    handleTeacherChange,
    refreshSelectedTeacher,
    refreshAll,

    openHoursDrawer,
    closeHoursDrawer,
    handleSaveCoachHour,
    handleUnregisterCoach,
  };
}